import {
    applyMiddleware,
    compose,
    Store,
    Middleware,
    MiddlewareAPI,
} from 'redux';
import createStore from 'services/createStore';
import createSentryMiddleware from 'redux-sentry-middleware';
import { values, map, forEach } from 'lodash';
import thunk from 'redux-thunk';
import { createEpicMiddleware, TypedEpic } from 'redux-observable';
import Immutable from 'immutable';
import { persistStore, persistReducer } from '@gin-betting/persist';
import core from '@gin-betting/core';
import bannersModule, { BannerTypes } from '@gin-betting/banners';

import { State } from 'types';
import favorite from 'modules/favorite';
import matchList from 'modules/match-list';
import ui from 'modules/ui';
import promo from 'modules/promo';
import createRootReducer from './createRootReducer';
import { matchListRoutes, isMobile } from 'app-constants';
import gc from 'services/garbage-collector';

const {
    services: {
        middlewareRegistry,
        reducerRegistry,
        rootEpic,
        registerEpic,
        sentryHub,
    },
    entities,
    viewport: {
        selectors: { selectIsCompact },
    },
} = core;

const { middleware: gcMiddleware } = gc;

const dynamicMiddlewares = ({ getState, dispatch }: MiddlewareAPI<any>) => (
    next: any
) => (action: any) => {
    const middlewareAPI = {
        getState,
        dispatch: (act: any) => dispatch(act),
    };

    // TODO: optimize access to dynamically added middlewares;
    //       this occurs on every dispatched action
    const chain = map(values(middlewareRegistry.getEntries()), middleware =>
        middleware(middlewareAPI)
    );

    return compose<any>(...chain)(next)(action);
};

const persistConfig = {
    key: 'root',
    whitelist: [],
    stateReconciler: false as const,
    debug: true,
};

export default function configureStore(): Store<State> {
    let middlewares: Middleware[] = [thunk];

    if (__DEV__) {
        const reduxLog = localStorage.getItem('redux-log');

        // TODO: move to separate file
        const devMiddleware: Middleware = ({ dispatch }) => next => (
            action: any
        ) => {
            const { type, payload } = action;
            const {
                actions: { setCmsBanners },
                constants: { GET_BANNERS_DATA_SUCCESS },
            } = bannersModule;

            const bannerStubs: [
                BannerTypes.BannerInfo,
                ...BannerTypes.BannerInfo[]
            ] = [
                {
                    id: 'banner:betting',
                    imageHref: '/en/news/just-bet-and-win-prizes',
                    imageSrc: '/assets/news/promo.png',
                    title: 'title 1',
                    buttonText: 'my bets',
                    buttonHref: '#!/player/profile-bets-history',
                },
                {
                    id: 'banner:banner-b2',
                    imageHref: '/en/news/just-bet-and-win-prizes',
                    imageSrc: '/assets/news/promo1.png',
                    title: 'title 1',
                    buttonText: '',
                    buttonHref: '',
                },
                {
                    id: 'banner:banner-b3',
                    imageHref: '/en/news/just-bet-and-win-prizes',
                    imageSrc: '/assets/news/promo1.png',
                    title: 'title 1',
                    buttonText: 'my bets 3',
                    buttonHref: '#!/player/profile-bets-history',
                },
            ];

            if (type === GET_BANNERS_DATA_SUCCESS) {
                const banners = (payload.data
                    .banners as BannerTypes.ApiBannerItem[])
                    .filter(b => b.type === 'cms_banner')
                    .map(({ id }, i) => ({
                        ...(bannerStubs[i] ?? bannerStubs[0]),
                        id,
                    }));

                if (banners.length > 0) {
                    setTimeout(() => {
                        dispatch(setCmsBanners({ banners }));
                    }, 3000);
                }
            }

            return next(action);
        };

        middlewareRegistry.register('devMiddleware', devMiddleware);

        if (reduxLog !== null) {
            import(/* webpackChunkName: "devtools" */ 'redux-logger').then(
                m => {
                    const loggerMiddleware = m.createLogger({
                        collapsed: true,
                        timestamp: false,
                        logErrors: true,
                        predicate: (getState, action) => {
                            if (
                                [
                                    'async/SUBSCRIBE_TO_UPDATES',
                                    'async/UNSUBSCRIBE_TO_UPDATES',
                                ].includes(action.type)
                            ) {
                                return false;
                            }

                            return (
                                reduxLog === '*' ||
                                action.type.includes(reduxLog)
                            );
                        },
                    });

                    middlewareRegistry.register('logger', loggerMiddleware);
                }
            );
        }
    }

    middlewares = middlewares.concat([
        dynamicMiddlewares,
        matchList.pagination.middleware(matchListRoutes),
        matchList.middleware(),
        entities.middleware(),
        core.appServiceLocator.get('apiMiddleware'),
        core.appServiceLocator.get('subscriptionMiddleware'),
        createEpicMiddleware(rootEpic as any),
        gcMiddleware(),
    ]);

    middlewares.push(
        createSentryMiddleware(sentryHub as any, {
            stateTransformer: (state: State) => {
                return state.delete('entities');
            },
        })
    );

    // @ts-ignore
    const devToolsExtension = window['__REDUX_DEVTOOLS_EXTENSION__'];

    const store = createStore<State>(
        persistReducer<State>(
            persistConfig,
            createRootReducer(reducerRegistry.getEntries())
        ),
        compose<any>(
            applyMiddleware(...middlewares),
            devToolsExtension
                ? devToolsExtension({
                      maxAge: getDevtoolActionsMaxAge(),
                      serialize: {
                          immutable: Immutable,
                      },
                  })
                : (f: any) => f
        ),
        undefined
    );

    const { requestLocalSport, ...otherFavoriteEpics } = favorite.epics;
    const { syncGamblerSettings, ...otherUIEpics } = ui.epics;
    const { makeRequestBanners } = bannersModule.epics;

    // TODO: fix epic type, 'Epic' is not assignable to type 'TypedEpic'
    registerEpic(requestLocalSport as any);
    forEach(otherFavoriteEpics, epic => registerEpic(epic));
    forEach(otherUIEpics, epic => registerEpic(epic));
    forEach(matchList.epics, epic => registerEpic(epic));
    registerEpic(makeRequestBanners());
    forEach(promo.epics, epic => registerEpic(epic));

    const isCompact = selectIsCompact(store.getState(), {});

    if (!isCompact && !isMobile) {
        registerEpic(syncGamblerSettings);
    }

    const persistor = persistStore(store);

    reducerRegistry.onRegistryChange(newReducers => {
        store.replaceReducer(
            persistReducer(persistConfig, createRootReducer(newReducers))
        );

        if (
            Object.keys(newReducers).some(
                // NOTE: check strictly for "false" value, not just empty
                // @ts-ignore
                name => newReducers[name]?.['_isPersisted'] === false
            )
        ) {
            persistor.persist();
        }
    });

    enableHotLoader(store);

    if (__DEV__ && __IS_MOCK__) {
        const mockName = localStorage.getItem('state-mock') || 'state';

        const state = require(`../../internals/stubs/${mockName}.json`);
        (store as any).liftedStore.dispatch({
            type: 'IMPORT_STATE',
            nextLiftedState: JSON.parse(state.payload),
        });
    }

    return store;
}

function enableHotLoader(store: Store<any>) {
    const isDev = typeof __DEV__ === 'string';

    if (!isDev) return;

    const { hot } = module as any;

    if (hot) {
        hot.accept('./createRootReducer', () => {
            const createNextRootReducer: any = require('./createRootReducer');
            store.replaceReducer(
                persistReducer(persistConfig, createNextRootReducer())
            );
        });
    }
}

function getDevtoolActionsMaxAge() {
    const defaultAge = 100;

    try {
        const age = localStorage && localStorage.getItem('devtool-max-age');

        return age ? +age : defaultAge;
    } catch (e) {
        return defaultAge;
    }
}
